import React, { createContext, useContext } from 'react';

// Real dashboard data that all agents reference
export const DASHBOARD_DATA = {
  roi: {
    marginRecovered: 87400,
    laborFreed: 2.1,
    fillRateImprovement: 2.3,
    supplierReliabilityImprovement: 3.1,
    monthlyAverageSavings: 18240,
    categoryBreakdown: {
      produce: 32000,
      dairy: 28900,
      frozen: 15600,
      grocery: 10700
    },
    laborBreakdown: {
      poChasingHours: 180,
      invoiceMatchingHours: 45,
      exceptionResearchHours: 22
    },
    paybackMonths: 4.2,
    annualRevenuRecovery: 280000
  },

  autonomy: {
    autonomyScore: 96,
    autonomousDecisions: 1247,
    escalations: 52,
    avgEscalationTime: 2.3,
    falsePositiveRate: 0.6,
    weeklyTrend: [
      { week: 1, autonomy: 87, escalations: 156 },
      { week: 2, autonomy: 91, escalations: 98 },
      { week: 3, autonomy: 94, escalations: 72 },
      { week: 4, autonomy: 96, escalations: 52 }
    ],
    escalationReasons: [
      { reason: 'User approval needed', count: 28, pct: 54 },
      { reason: 'Amount threshold exceeded', count: 16, pct: 31 },
      { reason: 'New supplier/unknown', count: 8, pct: 15 }
    ]
  },

  suppliers: [
    { id: 1, name: 'PT Segar Indonesia', score: 92, onTime: 95, orders: 34, status: 'excellent', trend: '+2%' },
    { id: 2, name: 'Fresh Farms Corp', score: 88, onTime: 92, orders: 28, status: 'good', trend: '-1%' },
    { id: 3, name: 'Agro Distributor', score: 84, onTime: 85, orders: 24, status: 'improving', trend: '+5%' },
    { id: 4, name: 'Impor Express', score: 71, onTime: 68, orders: 18, status: 'declining', trend: '-5%' },
    { id: 5, name: 'Local Produce', score: 65, onTime: 62, orders: 12, status: 'declining', trend: '-3%' }
  ],

  demand: {
    weeklyForecast: {
      produce: { baseline: 240, forecast: 260, confidence: 92 },
      dairy: { baseline: 180, forecast: 175, confidence: 88 },
      frozen: { baseline: 120, forecast: 118, confidence: 85 },
      grocery: { baseline: 300, forecast: 310, confidence: 91 }
    },
    trendAnalysis: {
      weekendDemand: '+8%',
      weekdayDemand: '-2%',
      seasonalFactors: 'back-to-school season trending'
    },
    posDataPoints: 342
  },

  orders: {
    thisWeekPos: 48,
    confirmedPos: 9,
    pendingPos: 3,
    averageLeadTime: 2.3,
    reliableSuppliersCount: 5
  },

  claims: {
    thisWeekFiled: 7,
    thisWeekRecovered: 2450,
    pendingClaims: 3,
    byCategory: {
      shortShips: { count: 4, amount: 1200 },
      priceErrors: { count: 2, amount: 850 },
      substitutions: { count: 1, amount: 400 }
    }
  },

  receiving: {
    todayProcessed: 24,
    matchRate: 100,
    avgProcessingTime: 4.2,
    discrepancies: 0
  }
};

const AgentDataContext = createContext(DASHBOARD_DATA);

export function AgentDataProvider({ children }) {
  return (
    <AgentDataContext.Provider value={DASHBOARD_DATA}>
      {children}
    </AgentDataContext.Provider>
  );
}

export function useAgentData() {
  return useContext(AgentDataContext);
}

// Agent query functions - each agent analyzes specific data
export const agentQueries = {
  demand: {
    getForecast: () => DASHBOARD_DATA.demand,
    analyzeTrends: () => {
      const data = DASHBOARD_DATA.demand;
      const highest = Object.entries(data.weeklyForecast)
        .sort(([, a], [, b]) => (b.forecast - b.baseline) - (a.forecast - a.baseline))[0];
      return {
        highestGrowth: highest[0],
        growth: highest[1].forecast - highest[1].baseline,
        confidence: highest[1].confidence
      };
    },
    getRecommendations: () => {
      const forecast = DASHBOARD_DATA.demand;
      const produce = forecast.weeklyForecast.produce;
      const growth = produce.forecast - produce.baseline;
      return {
        recommendation: `Increase supplier orders by ${Math.round((growth / produce.baseline) * 100)}% for Fri-Sun window`,
        reasoning: 'Weekend demand spike detected with 92% confidence',
        impactedCategories: ['produce', 'grocery']
      };
    }
  },

  ordering: {
    getOrderStatus: () => DASHBOARD_DATA.orders,
    getReliableSuppliers: () => {
      const suppliers = DASHBOARD_DATA.suppliers.filter(s => s.score >= 85);
      return {
        count: suppliers.length,
        suppliers: suppliers.map(s => ({ name: s.name, score: s.score })),
        recommendation: 'Allocate 60% of orders to top 3 suppliers'
      };
    },
    getPendingApprovals: () => ({
      pending: DASHBOARD_DATA.orders.pendingPos,
      avgLeadTime: DASHBOARD_DATA.orders.averageLeadTime,
      priority: 'High - should reach out to suppliers within 2 hours'
    })
  },

  claims: {
    getClaimsSummary: () => DASHBOARD_DATA.claims,
    getRecoveryStatus: () => {
      const claims = DASHBOARD_DATA.claims;
      return {
        recovered: claims.thisWeekRecovered,
        pending: claims.pendingClaims,
        topCategory: 'shortShips',
        amount: claims.byCategory.shortShips.amount,
        recommendation: 'Follow up on 3 pending short-ship claims - high recovery potential'
      };
    }
  },

  receiving: {
    getTodayStatus: () => DASHBOARD_DATA.receiving,
    getDiscrepancyRate: () => ({
      matchRate: DASHBOARD_DATA.receiving.matchRate,
      discrepancies: DASHBOARD_DATA.receiving.discrepancies,
      status: 'Perfect alignment - no issues detected'
    })
  },

  scoring: {
    getSupplierScores: () => DASHBOARD_DATA.suppliers,
    getDecliners: () => {
      const declining = DASHBOARD_DATA.suppliers.filter(s => s.trend.startsWith('-'));
      return {
        decliningCount: declining.length,
        suppliers: declining.map(s => ({ name: s.name, score: s.score, trend: s.trend })),
        recommendation: 'Monitor Impor Express and Local Produce - consider backup suppliers'
      };
    },
    getPerformanceGaps: () => {
      const suppliers = DASHBOARD_DATA.suppliers;
      const avgScore = suppliers.reduce((sum, s) => sum + s.score, 0) / suppliers.length;
      return {
        avgScore: Math.round(avgScore),
        topPerformer: suppliers[0],
        needsAttention: suppliers.filter(s => s.score < avgScore)
      };
    }
  },

  confirmation: {
    getPendingConfirmations: () => DASHBOARD_DATA.orders.pendingPos,
    getConfirmationRate: () => ({
      confirmed: DASHBOARD_DATA.orders.confirmedPos,
      pending: DASHBOARD_DATA.orders.pendingPos,
      confirmationRate: Math.round((DASHBOARD_DATA.orders.confirmedPos / (DASHBOARD_DATA.orders.confirmedPos + DASHBOARD_DATA.orders.pendingPos)) * 100),
      avgWaitTime: '1.2 hours',
      recommendation: 'Re-send via WhatsApp to 3 suppliers'
    })
  }
};
